import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ClipboardList, MapPin, Clock, DollarSign, Plus, ArrowRight } from "lucide-react";
import PublicLayout from "@/layouts/PublicLayout";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import StatusPill from "@/components/marketplace/StatusPill";
import { useDocumentTitle } from "@/lib/useDocumentTitle";

/**
 * Public "request board" — where providers browse jobs to bid on.
 * Also serves as the landing for `/requests`.
 */
export default function PublicRequestsPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [kind, setKind] = useState("");
  useDocumentTitle("Open project requests");

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (kind) params.kind = kind;
    api.get("/requests", { params }).then((r) => { setItems(r.data.items || []); setTotal(r.data.total || 0); }).finally(() => setLoading(false));
  }, [kind]);

  return (
    <PublicLayout>
      <div className="mx-auto max-w-6xl px-6 lg:px-10 py-12">
        <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
          <div>
            <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#165a71]">Open requests</div>
            <h1 className="mt-2 font-display text-3xl md:text-4xl font-semibold text-[#071b22]">Bid on Gulf Coast projects.</h1>
            <p className="text-[#252A34]/70 mt-2 text-sm">{total} live requests — send a tailored proposal in under 5 minutes.</p>
          </div>
          <Link to="/request-a-service" className="inline-flex items-center gap-2 rounded-full bg-[#071b22] text-white px-5 py-2.5 font-medium hover:bg-black" data-testid="new-request-cta">
            <Plus className="h-4 w-4" /> Post a request
          </Link>
        </div>

        <div className="flex items-center gap-2 mb-6 flex-wrap">
          {[{ k: "", label: "All" }, { k: "local", label: "Local" }, { k: "digital", label: "Digital" }, { k: "hybrid", label: "Hybrid" }].map((f) => (
            <button key={f.k} onClick={() => setKind(f.k)} className={`rounded-full px-4 py-1.5 text-sm border ${kind === f.k ? "bg-[#165a71] text-white border-[#165a71]" : "bg-white border-slate-200 text-[#252A34]"}`} data-testid={`filter-${f.k || "all"}`}>{f.label}</button>
          ))}
        </div>

        {loading ? (
          <div className="text-[#252A34]/60">Loading…</div>
        ) : items.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center">
            <ClipboardList className="h-6 w-6 mx-auto text-[#165a71]" />
            <div className="mt-2 font-display text-lg text-[#071b22] font-semibold">No open requests match.</div>
            <div className="text-sm text-[#252A34]/70 mt-1">Try a different filter or check back soon.</div>
          </div>
        ) : (
          <div className="grid gap-3">
            {items.map((r) => (
              <Link key={r.id} to={`/requests/${r.id}`} className="rounded-2xl border border-slate-200 bg-white p-5 flex items-start gap-4 hover:border-[#165a71]/40 transition-colors" data-testid={`request-row-${r.id}`}>
                <div className="h-12 w-12 rounded-xl bg-[#165a71]/10 flex items-center justify-center text-[#165a71]"><ClipboardList className="h-5 w-5" /></div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-3">
                    <div className="font-display font-semibold text-[#071b22] truncate">{r.title}</div>
                    <StatusPill status={r.status} />
                  </div>
                  <div className="text-sm text-[#252A34]/70 mt-1 line-clamp-2">{r.description}</div>
                  <div className="mt-3 text-xs text-[#252A34]/60 flex flex-wrap items-center gap-3">
                    <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {r.urgency?.replace(/_/g, " ")}</span>
                    {r.address_snapshot?.city && <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {r.address_snapshot.city}, {r.address_snapshot.state}</span>}
                    <span className="inline-flex items-center gap-1"><DollarSign className="h-3.5 w-3.5" /> {
                      r.budget_type === "hourly" ? `$${r.budget_hourly}/hr` :
                      r.budget_type === "open" ? "Open" :
                      `$${r.budget_min ?? 0}-$${r.budget_max ?? "?"}`
                    }</span>
                    <span>· {r.proposal_count || 0} proposals</span>
                  </div>
                </div>
                <ArrowRight className="h-4 w-4 text-[#165a71] mt-2 shrink-0" />
              </Link>
            ))}
          </div>
        )}
      </div>
    </PublicLayout>
  );
}
